import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../../../context/ThemeContext';
import { Cita } from '../../../../services/citasService';

interface Props { reservation: Cita; onClose: () => void; onConfirm: (motivo: string) => Promise<void>; }
export default function CancelReservationModal({ reservation, onClose, onConfirm }: Props) {
    const { isDark } = useTheme();
    const [motivo, setMotivo] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const dialog = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const previous = document.activeElement as HTMLElement | null;
        dialog.current?.querySelector('textarea')?.focus();
        return () => previous?.focus();
    }, []);
    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (busy || !motivo.trim()) return;
        setBusy(true); setError('');
        try { await onConfirm(motivo.trim()); }
        catch (reason: any) {
            const data = reason?.response?.data;
            setError(String(data?.detail || data?.motivo || 'No se pudo cancelar la reserva. Intenta nuevamente.'));
            setBusy(false);
        }
    };
    return createPortal(<div style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(22,10,43,.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div ref={dialog} role="dialog" aria-modal="true" aria-labelledby="cancel-reservation-title" onKeyDown={event => {
            if (event.key === 'Escape' && !busy) onClose();
            if (event.key === 'Tab') {
                const elements = dialog.current?.querySelectorAll<HTMLElement>('textarea, button:not(:disabled)');
                if (!elements?.length) return;
                const first = elements[0], last = elements[elements.length - 1];
                if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
                else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
            }
        }} style={{ width: '100%', maxWidth: 480, borderRadius: 24, padding: 28, background: isDark ? '#160a2b' : '#fff', color: isDark ? '#fff' : '#0f172a', border: '1px solid #C1348B55', boxShadow: '0 20px 60px #0004' }}>
            <h2 id="cancel-reservation-title" style={{ fontSize: '1.25rem', fontWeight: 800 }}>¿Deseas cancelar tu reserva?</h2>
            <p style={{ margin: '12px 0', fontSize: '.9rem' }}>Reserva {reservation.codigo || `#${reservation.id}`}. La cancelación liberará el espacio reservado.</p>
            <form onSubmit={submit}>
                <label htmlFor="cancel-reason" style={{ fontWeight: 700 }}>Motivo de cancelación</label>
                <textarea id="cancel-reason" required maxLength={500} value={motivo} readOnly={busy} onChange={event => setMotivo(event.target.value)} rows={4} style={{ display: 'block', width: '100%', margin: '8px 0 16px', border: '1px solid #C1348B55', borderRadius: 12, padding: 12, background: isDark ? '#211033' : '#faf7fc', color: 'inherit', resize: 'vertical' }} />
                {error && <p role="alert" style={{ color: '#C1348B', marginBottom: 12 }}>{error}</p>}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                    <button type="button" disabled={busy} onClick={onClose} style={{ border: '1px solid #61257B55', borderRadius: 10, padding: '10px 15px', color: isDark ? '#FFD97C' : '#61257B', background: 'transparent', fontWeight: 700 }}>Volver</button>
                    <button type="submit" disabled={busy || !motivo.trim()} style={{ border: 0, borderRadius: 10, padding: '10px 15px', background: 'linear-gradient(135deg,#61257B,#C1348B)', color: '#fff', fontWeight: 700, opacity: busy || !motivo.trim() ? .6 : 1 }}>{busy ? 'Cancelando…' : 'Cancelar reserva'}</button>
                </div>
            </form>
        </div>
    </div>, document.body);
}
